package one.healthium.tasks.ui

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import loco.DAILY_TASKS_SCREEN_TITLE
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTaskType
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppLinearProgressIndicator
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.modifier.adaptiveWidthTwoPane
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.max

interface DailyTasksEventDelegate {
    fun onLogWeightTaskClicked(taskId: String)
    fun onLogWaterTaskClicked(taskId: String)
    fun onLogFoodTaskClicked(taskId: String)
    fun onReadInsightTaskClicked(insightId: String)
    fun onDoWorkoutTaskClicked(workoutId: String)
    fun onLogSymptomsTaskClicked(taskId: String)
}

@Composable
fun DailyTasksScreen(eventDelegate: DailyTasksEventDelegate) {
    val viewModel = koinViewModel<DailyTasksViewModel>()
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.markDailyTasksAsRead()
    }

    DailyTasksScreenContent(
        state = state,
        onTaskClicked = { task ->
            when (val dailyTaskType = task.info.type) {
                DailyTaskType.LogWeight -> eventDelegate.onLogWeightTaskClicked(task.id)
                DailyTaskType.LogWater -> eventDelegate.onLogWaterTaskClicked(task.id)
                DailyTaskType.LogFood -> eventDelegate.onLogFoodTaskClicked(task.id)
                is DailyTaskType.ReadInsight -> eventDelegate.onReadInsightTaskClicked(dailyTaskType.id)
                is DailyTaskType.DoWorkout -> eventDelegate.onDoWorkoutTaskClicked(dailyTaskType.id)
                DailyTaskType.LogSymptoms -> eventDelegate.onLogSymptomsTaskClicked(task.id)
            }
        },
    )

    AlertUiMessageHandler(viewModel.uiMessage)
}

@Composable
internal fun DailyTasksScreenContent(
    state: DailyTasksUiState,
    onTaskClicked: (DailyTaskModel) -> Unit,
) {
    AppScaffold(
        modifier = Modifier.testTag("daily_tasks_screen"),
        topBar = {
            AppTopBar(
                expandedHeight = 80.dp,
                title = {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Spacer(Modifier.height(10.dp))
                        Text(stringResource(loco.Res.string.DAILY_TASKS_SCREEN_TITLE))
                        Spacer(Modifier.height(16.dp))

                        val animatedProgress by animateFloatAsState(
                            targetValue = max(0.03f, state.progress),
                            animationSpec = tween(durationMillis = 500),
                        )
                        AppLinearProgressIndicator(
                            modifier = Modifier
                                .height(6.dp)
                                .width(240.dp)
                                .testTag("daily_tasks_progress_bar"),
                            color = AppTheme.dailyTaskStyle.progressBarColorOrDefault,
                            trackColor = AppTheme.dailyTaskStyle.progressBarTrackColorOrDefault,
                            progress = { animatedProgress },
                        )
                        Spacer(Modifier.height(22.dp))
                    }
                },
            )
        }
    ) {
        Box(modifier = Modifier.fillMaxSize().padding(it)) {
            DailyTasksList(
                modifier = Modifier.align(Alignment.TopCenter).adaptiveWidthTwoPane(),
                uiModel = state.tasksUiModel,
                onTaskClicked = onTaskClicked,
            )

            AppLoadingScreen(visible = state.loading)
        }
    }
}
